import { useEffect, useMemo, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';

import {
  Box,
  Banner,
  Flex,
  OneColumnLayout,
  Spacer,
  COUNTRIES,
} from '@electricjs/arc';
import {
  EmployeeStatusEnum,
  EntitlementSlugEnum,
  HRISProviderSlugEnum,
  PackageNameEnum,
  PermissionEnum,
  RequestStatusEnum,
} from '@electricjs/core_entity-client';
import { useAppSelector } from '@/redux/store';

import { CenteredSpinner } from '@common';
import { InitFailedState } from '@/components/Errors/InitFailedState';
import { useGlobalUI } from '@/components/GlobalUIProvider';
import AddLicensesModal from '@/components/Licenses/AddLicensesModal';
import Applications from '@/components/Onboardings/Applications';
import Demographics from '@/components/Onboardings/Demographics';
import EmployeeInformation from '@/components/Onboardings/EmployeeInformation';
import LicenseSelection from '@/components/Onboardings/LicenseSelection';
import Onboarding from '@/components/Onboardings/Onboarding';
import { getOnboardingSchema } from '@/components/Onboardings/OnboardingSchema';
import {
  HiringStatus,
  OnboardingFormData,
  OnboardingStep,
} from '@/components/Onboardings/types';
import {
  EXISTING_EMPLOYEE_ID_QUERY_PARAM,
  HRIS_ONBOARDING_REQUEST_ID_QUERY_PARAM,
} from '@/constants/onboarding';
import useAddEmployeeToGroups from '@/hooks/useAddEmployeeToGroups';
import { useGetOrganizationId } from '@/hooks/useGetOrganizationId';
import { useHasProPlan } from '@/hooks/useHasProPlan';
import { useUpdateRequestStatus } from '@/hooks/useUpdateRequestStatus';
import { useUserHasOrganizationPermission } from '@/hooks/useUserHasOrganizationPermission';
import {
  useBulkUpdateEmployeesMutation,
  useLazyGetEmployeeQuery,
  useLazyGetOrganizationGroupsForEmployeeQuery,
} from '@/redux/slices/employeeApiSlice';
import { useCreateOnboardingMutation } from '@/redux/slices/onboardingApiSlice';
import {
  useAddOrganizationEmployeesMutation,
  useGetOrganizationAdditionalInfoQuery,
} from '@/redux/slices/organizationApiSlice';
import {
  useGetOrganizationPackagesQuery,
  useSendChangeLicenseCountInterestNotificationMutation,
} from '@/redux/slices/packageApiSlice';
import { Group } from '@/types/groups';
import {
  ApplicationsAutomationPreferenceEnum,
  HardwareProvisioningPreferenceEnum,
} from '@/types/organizations';
import SpinnerWithMessage from 'common/SpinnerWithMessage';
import { getFullName } from 'common/utils/getFullName';
import { getEmployeeData } from './helpers';
import { useOrganizationHasEntitlement } from '@/hooks/useOrganizationHasEntitlement';

export type AddEmployeeParams = {
  onSuccess: (employeeId: string) => void;
  status?: EmployeeStatusEnum;
  organizationPackageId?: string;
  createUser?: boolean;
};

const NewOnboarding = () => {
  const { t, i18n } = useTranslation(['pages', 'common']);
  const {
    ithEmployeeUpdatesSettings,
    ithNewItSetup,
    selfServiceSeatAmendment,
  } = useFlags();
  const canOnboard = useUserHasOrganizationPermission(
    PermissionEnum.Onboardingswrite
  );
  const [employeeId, setEmployeeId] = useState('');
  const [hasAvailableLicenses, setHasAvailableLicenses] = useState(true);
  // Tracks whether this session's new hire already had a license assigned, so the
  // org-wide "no licenses" banner doesn't reappear for them once their own license is secured.
  const [hasAssignedLicenseThisSession, setHasAssignedLicenseThisSession] =
    useState(false);
  const [addOrganizationEmployees] = useAddOrganizationEmployeesMutation();
  const { addEmployeeToGroups, isLoading: isAddingEmployee } =
    useAddEmployeeToGroups();
  const [createOnboarding, { isLoading: isCreatingOnboarding }] =
    useCreateOnboardingMutation();

  const organizationId = useGetOrganizationId();
  const organizationHrisProviderSlug = useAppSelector(
    state => state.loggedUser?.organizationHrisIntegration?.hrisProvider?.slug
  );
  const isTrinetOrg =
    organizationHrisProviderSlug === HRISProviderSlugEnum.TrinetPeodirect;
  const hasProPlan = useHasProPlan();
  const {
    data: organizationPackages,
    isFetching: isFetchingOrganizationPackages,
    isError: isOrganizationPackagesError,
  } = useGetOrganizationPackagesQuery(organizationId);
  // Keeping this highly restricted to the current posible scenarios right now
  // to avoid any side effects derived from unwanted states
  const isMultiLicense =
    (organizationPackages ?? []).some(
      pkg => pkg.package.name === PackageNameEnum.Pro
    ) &&
    (organizationPackages ?? []).some(
      pkg => pkg.package.name === PackageNameEnum.Lyte
    );

  const { showSuccessToast, showErrorToast, openModal, closeModal } =
    useGlobalUI();
  const navigate = useNavigate();
  const hasXboardingsEntitlement = useOrganizationHasEntitlement(
    EntitlementSlugEnum.Xboardings
  );

  const DEFAULT_COUNTRY_CODE = i18n.language.startsWith('fr')
    ? COUNTRIES.CA.code
    : COUNTRIES.US.code;

  const defaultValues = {
    demographics: {
      firstName: '',
      lastName: '',
      personalEmail: '',
      address: {
        streetAddress1: '',
        streetAddress2: '',
        city: '',
        state: '',
        country: DEFAULT_COUNTRY_CODE,
        zip: '',
      },
      phone: {
        phoneNumber: '',
        country: DEFAULT_COUNTRY_CODE,
      },
    },
    employeeInformation: {
      startDate: null,
      jobTitle: '',
      email: '',
    },
    currentStep: 1,
    hiringStatus: hasXboardingsEntitlement
      ? HiringStatus.New
      : HiringStatus.Existing,
    needsApplications: false,
    needsHardware: false,
    groups: [],
    organizationPackageId: undefined,
    watchers: [],
    notifyManager: true,
    additionalApps: [],
    excludedApps: [],
  };

  const methods = useForm<OnboardingFormData>({
    defaultValues,
    mode: 'onTouched',
    // @ts-expect-error: This is a known issue with yupResolver.
    // It has been fixed in version 3.3.0, but the update would affect multiple forms in the application where the typing is not accurate.
    //https://github.com/react-hook-form/resolvers/issues/567#issuecomment-1688110169
    resolver: yupResolver(getOnboardingSchema(t)),
  });
  const { control, getValues, handleSubmit, setValue, reset } = methods;

  const [
    currentStep,
    hiringStatus,
    organizationPackageId,
    addressCountry,
    phone,
    additionalApps,
    excludedApps,
  ] = useWatch({
    control,
    name: [
      'currentStep',
      'hiringStatus',
      'organizationPackageId',
      'demographics.address.country',
      'demographics.phone',
      'additionalApps',
      'excludedApps',
    ],
  });

  useEffect(() => {
    const proPackage = organizationPackages?.find(
      pkg => pkg.package.name === PackageNameEnum.Pro
    );
    const essentialsPackage = organizationPackages?.find(
      pkg => pkg.package.name === PackageNameEnum.Lyte
    );

    // When the organization has multiple packages, we need to check if there are available licenses for the PRO package to set it as the default license type.
    if (isMultiLicense) {
      const hasAvailableProLicenses =
        !proPackage?.licenseCount ||
        (proPackage?.availableLicenseCount !== null &&
          proPackage?.availableLicenseCount > 0);

      if (hasAvailableProLicenses) {
        setValue('organizationPackageId', proPackage?.id);
        return;
      }

      // If there are no available licenses for the PRO package, check if there are available licenses for the ESSENTIALS package.
      const hasAvailableEssentialsLicenses =
        !essentialsPackage?.licenseCount ||
        (essentialsPackage?.availableLicenseCount !== null &&
          essentialsPackage?.availableLicenseCount > 0);

      if (hasAvailableEssentialsLicenses) {
        setValue('organizationPackageId', essentialsPackage?.id);
        return;
      }

      // If there are no available licenses for the PRO or ESSENTIALS package, set the hasAvailableLicenses state to false, in order to show the banner.
      setHasAvailableLicenses(false);
      return;
    }

    // When the organization has a PRO or ESSENTIALS package (but not both), check that specific
    // package's availability instead of assuming organizationPackages[0] is the relevant one.
    const relevantPackage =
      proPackage ?? essentialsPackage ?? organizationPackages?.[0];

    const hasAvailableLicenses =
      (relevantPackage?.availableLicenseCount ?? 0) > 0 ||
      !relevantPackage?.licenseCount;

    if (hasAvailableLicenses) {
      setValue('organizationPackageId', relevantPackage?.id);
    }

    // Set the state to display the banner according to the availability of licenses.
    setHasAvailableLicenses(hasAvailableLicenses);
  }, [isMultiLicense, organizationPackages, setValue]);

  const isNewHire =
    hiringStatus === HiringStatus.New || hiringStatus === HiringStatus.Hris;

  const handleErrorState = (logError?: string) => {
    showErrorToast({
      id: 'add-employee-error-toast',
      message: t('pages:onboarding.errorAdding'),
      stack: true,
    });

    if (logError) {
      window?.DD_RUM?.addError(logError, {
        location: 'Add New Employee',
      });

      console.error(logError);
    }
  };

  // `addEmployee` accepts a callback function `onSuccess` that is called with the `employeeId` as its argument if the employee is successfully added.
  // The employeeId is used to navigate to the employee's profile page and to determine the successful addition of the employee before proceeding in the child components.
  const addEmployee = ({
    onSuccess,
    status,
    organizationPackageId,
    createUser = true,
  }: AddEmployeeParams): void => {
    handleSubmit(async (formData: OnboardingFormData) => {
      const employeeData = getEmployeeData(formData);
      try {
        const addEmployeeResponse = await addOrganizationEmployees({
          organizationId,
          employees: [{ ...employeeData, status, organizationPackageId }],
          createUser,
        }).unwrap();

        if (addEmployeeResponse?.failedEmployeeEmails?.length) {
          const errorMessage = 'Error while adding new employee.';
          handleErrorState(errorMessage);
          return;
        }

        const employeeId = addEmployeeResponse?.addedEmployees?.[0]?.id;
        setEmployeeId(employeeId);
        if (organizationPackageId) {
          setHasAssignedLicenseThisSession(true);
        }
        onSuccess(employeeId);
      } catch (error) {
        handleErrorState(
          `Error while adding new employee: ${JSON.stringify(error)}`
        );
      }
    })();
  };

  // `addToGroups` receives the employeeGroups selected by the user and a callback function to be executed after the employee is added to the groups.
  const addToGroups = async (
    employeeGroups: Group[],
    onSuccess: () => void
  ) => {
    try {
      await addEmployeeToGroups(employeeId, employeeGroups);
      onSuccess();
    } catch (error) {
      handleErrorState(
        `Error while adding the employee to groups: ${JSON.stringify(error)}`
      );
    }
  };

  // Is this onboarding for an existing employee?
  // (probably one that we ingested from an HRIS and is currently in UNKNOWN status)
  const [queryParams] = useSearchParams();

  const existingEmployeeId =
    queryParams.get(EXISTING_EMPLOYEE_ID_QUERY_PARAM) ?? '';
  const hrisOnboardingRequestId =
    queryParams.get(HRIS_ONBOARDING_REQUEST_ID_QUERY_PARAM) ?? '';

  // Organization preferences apply only when this page was opened from the
  // HRIS-created Complete IT setup task. Manually-started IT setup URLs do not
  // include `hrisOnboardingRequestId` and keep their existing behavior.
  const shouldApplyITOnboardingPreferences = !!(
    ithEmployeeUpdatesSettings &&
    hasXboardingsEntitlement &&
    existingEmployeeId &&
    hrisOnboardingRequestId
  );

  const {
    data: employeeUpdatesSettings,
    isFetching: isFetchingEmployeeUpdatesSettings,
    isError: isEmployeeUpdatesSettingsError,
  } = useGetOrganizationAdditionalInfoQuery(
    { organizationId },
    { skip: !shouldApplyITOnboardingPreferences }
  );

  const applicationsPreference =
    employeeUpdatesSettings?.applicationsAutomationPreference ??
    ApplicationsAutomationPreferenceEnum.ManualReview;
  const hardwarePreference =
    employeeUpdatesSettings?.hardwareProvisioningPreference ??
    HardwareProvisioningPreferenceEnum.LetMeDecide;
  const isHardwarePreferenceFixed =
    hardwarePreference !== HardwareProvisioningPreferenceEnum.LetMeDecide;
  const shouldLandOnApplications =
    shouldApplyITOnboardingPreferences &&
    applicationsPreference ===
      ApplicationsAutomationPreferenceEnum.ManualReview &&
    isHardwarePreferenceFixed;
  const shouldChooseHardwareWithAutomatedApplications =
    shouldApplyITOnboardingPreferences &&
    applicationsPreference === ApplicationsAutomationPreferenceEnum.Automated &&
    hardwarePreference === HardwareProvisioningPreferenceEnum.LetMeDecide;

  // The other two combinations need no frontend routing: Manual + Let me
  // decide keeps the existing task, while Automated + fixed hardware is
  // completed by the ingestion workflow without opening this page.

  useEffect(() => {
    if (
      addressCountry &&
      addressCountry !== '' &&
      phone?.phoneNumber === '' &&
      !existingEmployeeId
    ) {
      setValue('demographics.phone.country', addressCountry, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [addressCountry, setValue, existingEmployeeId, phone?.phoneNumber]);

  const [
    getEmployee,
    {
      data: existingEmployee,
      isFetching: isFetchingEmployee,
      isError: isEmployeeError,
    },
  ] = useLazyGetEmployeeQuery();

  const [
    getGroupsForEmployee,
    {
      data: existingEmployeeGroups,
      isFetching: isFetchingGroups,
      isError: isEmployeeGroupsError,
    },
  ] = useLazyGetOrganizationGroupsForEmployeeQuery();

  /**
   * True when this onboarding is for an existing, HRIS-ingested employee who is
   * already Active and has a future start date, the org uses Trinet, and the
   * customer has a PRO plan.
   *
   * In that case we skip demographics/license and land on the Applications step,
   * and show the "Skip application setup" option so the user can add the employee
   * to groups and complete without creating onboarding requests (e.g. for employees
   * activated while handling hardware orders).
   */
  const isHRISActiveEmployeeOnboarding = useMemo(() => {
    if (!existingEmployeeId || !existingEmployee) return false;
    const employeeIsActive =
      existingEmployee.status === EmployeeStatusEnum.Active;
    const employeeHasFutureStartDate =
      !!existingEmployee.startDate &&
      new Date(existingEmployee.startDate) > new Date();
    return (
      employeeIsActive &&
      employeeHasFutureStartDate &&
      isTrinetOrg &&
      hasProPlan
    );
  }, [existingEmployeeId, existingEmployee, isTrinetOrg, hasProPlan]);

  // Fires exactly once per `existingEmployeeId`: kicks off the employee and
  // groups fetches. Deliberately excludes `isFetchingGroups`/
  // `existingEmployeeGroups`/`existingEmployee` from its dependencies --
  // including them would re-trigger these fetches every time the request
  // they themselves started resolves, looping forever.
  useEffect(() => {
    if (!existingEmployeeId) return;
    // If we have an existing employee ID passed in the URL, this is the employee we're dealing with
    setEmployeeId(existingEmployeeId);

    // Fetch the employee and their groups
    getEmployee({ organizationId, employeeId: existingEmployeeId });
    getGroupsForEmployee({ organizationId, employeeId: existingEmployeeId });
  }, [existingEmployeeId, organizationId, getEmployee, getGroupsForEmployee]);

  useEffect(() => {
    // Set the form values to the existing employee's data
    // This will bypass the first two steps of the onboarding flow, where we'd usually collect this info.
    // In this case, we've already retrieved it from the HRIS.
    //
    // `existingEmployeeGroups` is a dependency so this can self-correct as
    // `existingEmployee`/`existingEmployeeGroups` resolve (they're fetched
    // independently and may settle in either order). But once the form has
    // reached the Applications step, that step force-refetches this same
    // employee's groups (to pick up newly-matched dynamic groups), which
    // updates this same cache entry and would otherwise re-fire this reset()
    // and wipe out any groups the admin has since added/removed. Guarding on
    // `currentStep !== Applications` stops that specific collision while
    // still allowing normal self-correction on every earlier step.
    //
    // The step is read with getValues() rather than the watched `currentStep`
    // on purpose: as a dependency it re-ran this reset on every step change,
    // so leaving License Selection reset the form straight back to it
    // (multi-license orgs got stuck on "Assign a license").
    if (
      existingEmployee &&
      existingEmployeeId &&
      getValues('currentStep') !== OnboardingStep.Applications &&
      (!shouldApplyITOnboardingPreferences || employeeUpdatesSettings)
    ) {
      let nextStep;
      if (
        shouldLandOnApplications ||
        shouldChooseHardwareWithAutomatedApplications
      ) {
        nextStep = shouldLandOnApplications
          ? OnboardingStep.Applications
          : OnboardingStep.Onboarding;
      } else if (isMultiLicense) {
        nextStep = OnboardingStep.LicenseSelection;
      } else if (isHRISActiveEmployeeOnboarding) {
        nextStep = OnboardingStep.Applications;
      } else {
        nextStep = OnboardingStep.Onboarding;
      }
      reset(
        {
          demographics: {
            firstName: existingEmployee.firstName ?? '',
            lastName: existingEmployee.lastName ?? '',
            personalEmail: existingEmployee.personalEmail ?? '',
            address: {
              streetAddress1: existingEmployee.address?.streetAddress1 ?? '',
              streetAddress2: existingEmployee.address?.streetAddress2 ?? '',
              city: existingEmployee.address?.city ?? '',
              state: existingEmployee.address?.state ?? '',
              country: existingEmployee.address?.country ?? '',
              zip: existingEmployee.address?.zip ?? '',
            },
            phone: existingEmployee.phone
              ? { phoneNumber: existingEmployee.phone }
              : null,
          },
          employeeInformation: {
            startDate: existingEmployee.startDate
              ? new Date(existingEmployee.startDate)
              : null,
            jobTitle: existingEmployee.jobTitle ?? '',
            email: existingEmployee.email ?? '',
          },
          groups: existingEmployeeGroups ?? [],
          currentStep: nextStep,
          hiringStatus: HiringStatus.Hris,
          ...((shouldLandOnApplications ||
            shouldChooseHardwareWithAutomatedApplications) && {
            needsApplications: true,
            needsHardware:
              hardwarePreference !== HardwareProvisioningPreferenceEnum.Skip,
          }),
        },
        // Don't reset the defaultValues, only update the form values
        { keepDefaultValues: true }
      );
    }
  }, [
    existingEmployeeId,
    setValue,
    reset,
    existingEmployee,
    existingEmployeeGroups,
    getValues,
    isMultiLicense,
    isTrinetOrg,
    isHRISActiveEmployeeOnboarding,
    shouldApplyITOnboardingPreferences,
    employeeUpdatesSettings,
    shouldLandOnApplications,
    shouldChooseHardwareWithAutomatedApplications,
    hardwarePreference,
  ]);

  const [bulkUpdateEmployees, { isLoading: isUpdatingEmployees }] =
    useBulkUpdateEmployeesMutation();
  const { updateRequest, updateRequestLoading } = useUpdateRequestStatus({
    requestId: hrisOnboardingRequestId,
  });

  const submitOnboarding = async (
    employeeId: string,
    onSuccess: () => void,
    includesHardware?: boolean,
    includesApplications?: boolean,
    watchers?: string[]
  ) => {
    if (existingEmployeeId) {
      // If the employee already existed prior to the beginning of this flow, activate them
      await bulkUpdateEmployees({
        organizationId,
        employees: [
          {
            id: employeeId,
            status: EmployeeStatusEnum.Active,
            // If the organization has multiple packages, the selected package id is used.
            // Otherwise, the package id is undefined and the default value will be set in the backend.
            organizationPackageId,
          },
        ],
      });
    }
    const needsOnboarding = includesHardware || includesApplications;
    try {
      if (needsOnboarding) {
        // Only the redesigned IT Setup exposes a manager-notify toggle; in the
        // legacy flow there is no UI to control it, so don't silently append
        // the manager's id to watchers when the flag is off.
        let finalWatchers = watchers;
        if (ithNewItSetup) {
          const { notifyManager, employeeInformation } = getValues();
          const managerId = employeeInformation.manager?.value;
          if (notifyManager && managerId) {
            finalWatchers = Array.from(
              new Set([...(watchers ?? []), managerId])
            );
          }
        }

        await createOnboarding({
          organizationId,
          employeeId,
          includesHardware,
          watchers: finalWatchers,
          additionalAppKeys: (additionalApps ?? []).map(app => app.slug),
          deselectedAppKeys: (excludedApps ?? []).map(app => app.slug),
        }).unwrap();
      }

      // If it exists, mark HRIS onboarding request as completed
      if (hrisOnboardingRequestId) {
        await updateRequest({
          newStatus: RequestStatusEnum.Completed,
        });
      }

      const [firstName, lastName] = getValues([
        'demographics.firstName',
        'demographics.lastName',
      ]);
      const fullName = getFullName(firstName, lastName);

      showSuccessToast({
        id: 'onboard-employee-success-toast',
        message: `${fullName} was onboarded successfully.`,
        stack: true,
      });

      onSuccess();
    } catch (error) {
      handleErrorState(
        `Unable to submit onboarding:  ${JSON.stringify(error)}`
      );
    }
  };

  const [sendChangeLicenseCountInterestNotification] =
    useSendChangeLicenseCountInterestNotificationMutation();

  const handleSendLicencesNotification = () => {
    sendChangeLicenseCountInterestNotification({ organizationId })
      .unwrap()
      .catch(error => {
        console.error(`Unable to send email for `, error);
        showErrorToast({
          id: 'license-send-email-error-toast',
          message: t('pages:onboarding.licenseLimit.notifyError'),
          stack: true,
        });
      })
      .then(() => {
        showSuccessToast({
          id: 'license-send-email-success-toast',
          message: t('pages:onboarding.licenseLimit.notifySuccess'),
          stack: true,
        });
      });
  };

  const handleSetInactive = async () => {
    if (!existingEmployeeId) {
      addEmployee({
        onSuccess: () => {
          navigate('/active-employees');
        },
        status: EmployeeStatusEnum.Inactive,
      });
    } else {
      await bulkUpdateEmployees({
        organizationId,
        employees: [
          { id: existingEmployeeId, status: EmployeeStatusEnum.Inactive },
        ],
      });

      if (hrisOnboardingRequestId) {
        await updateRequest({
          newStatus: RequestStatusEnum.Completed,
        });
      }

      navigate('/active-employees');
    }
  };

  // isFetchingGroups/isEmployeeGroupsError reflect this component's own
  // subscription to the SAME cache entry (organizationId + employeeId) that
  // the Applications step independently force-refetches on mount to pick up
  // newly-matched dynamic groups. Once the user has reached that step, its
  // own refetch lifecycle (loading/error) shouldn't tear down the rest of
  // the onboarding page -- Applications owns that fetch from here on.
  const isOnApplicationsStep = currentStep === OnboardingStep.Applications;

  if (
    isFetchingEmployee ||
    (!isOnApplicationsStep && isFetchingGroups) ||
    isFetchingOrganizationPackages ||
    (shouldApplyITOnboardingPreferences && isFetchingEmployeeUpdatesSettings)
  ) {
    return <CenteredSpinner />;
  }

  if (
    isOrganizationPackagesError ||
    isEmployeeError ||
    (!isOnApplicationsStep && isEmployeeGroupsError) ||
    (shouldApplyITOnboardingPreferences && isEmployeeUpdatesSettingsError)
  ) {
    return <InitFailedState />;
  }

  if (updateRequestLoading || isCreatingOnboarding || isUpdatingEmployees) {
    return (
      <SpinnerWithMessage
        message={t('pages:onboarding.onboardingInProgress')}
      />
    );
  }

  return (
    <OneColumnLayout>
      {!hasAvailableLicenses && !hasAssignedLicenseThisSession && (
        <Box margin="0 auto" width="100%" maxWidth="72rem">
          <Banner
            id="banner-no-licenses"
            intent="warning"
            width="100%"
            isFlat
            title={t('pages:onboarding.licenseLimit.title')}
            message={t(
              selfServiceSeatAmendment
                ? 'pages:onboarding.licenseLimit.messageSelfService'
                : 'pages:onboarding.licenseLimit.message'
            )}
            onClickCta={
              selfServiceSeatAmendment
                ? () => openModal(<AddLicensesModal onClose={closeModal} />)
                : handleSendLicencesNotification
            }
            cta={
              selfServiceSeatAmendment
                ? t('pages:onboarding.licenseLimit.addLicensesButton')
                : t('pages:onboarding.licenseLimit.notifyButton')
            }
          />
          <Spacer y="3rem" />
        </Box>
      )}
      {(ithNewItSetup || shouldChooseHardwareWithAutomatedApplications) &&
      currentStep === OnboardingStep.Onboarding ? (
        <FormProvider {...methods}>
          <Onboarding
            addEmployee={addEmployee}
            submitOnboarding={submitOnboarding}
            existingEmployeeId={existingEmployeeId}
            hrisOnboardingRequestId={hrisOnboardingRequestId}
            handleSetInactive={handleSetInactive}
            isMultiLicense={isMultiLicense}
            existingEmployeeGroups={existingEmployeeGroups}
            isApplicationAutomationEnabled={
              shouldChooseHardwareWithAutomatedApplications
            }
            disableNext={
              !hasAvailableLicenses &&
              existingEmployee?.status !== EmployeeStatusEnum.Active
            }
          />
        </FormProvider>
      ) : (
        <Flex maxWidth="72rem" width="100%" margin="0 auto" vertical>
          <FormProvider {...methods}>
            {currentStep === OnboardingStep.Demographics && <Demographics />}
            {currentStep === OnboardingStep.EmployeeInformation && (
              <EmployeeInformation
                addEmployee={addEmployee}
                isNewHire={isNewHire}
                canOnboard={canOnboard}
                isMultiLicense={isMultiLicense}
                hasAvailableLicenses={hasAvailableLicenses}
                handleSetInactive={handleSetInactive}
              />
            )}
            {currentStep === OnboardingStep.LicenseSelection && (
              <LicenseSelection
                addEmployee={addEmployee}
                handleSetInactive={handleSetInactive}
                canOnboard={canOnboard}
                existingEmployeeId={existingEmployeeId}
                hrisOnboardingRequestId={hrisOnboardingRequestId}
                isHRISActiveEmployeeOnboarding={isHRISActiveEmployeeOnboarding}
              />
            )}
            {currentStep === OnboardingStep.Onboarding && (
              <Onboarding
                addEmployee={addEmployee}
                submitOnboarding={submitOnboarding}
                existingEmployeeId={existingEmployeeId}
                hrisOnboardingRequestId={hrisOnboardingRequestId}
                handleSetInactive={handleSetInactive}
                isMultiLicense={isMultiLicense}
                existingEmployeeGroups={existingEmployeeGroups}
                isApplicationAutomationEnabled={
                  shouldChooseHardwareWithAutomatedApplications
                }
                disableNext={
                  !hasAvailableLicenses &&
                  existingEmployee?.status !== EmployeeStatusEnum.Active
                }
              />
            )}
            {currentStep === OnboardingStep.Applications && (
              <Applications
                employeeId={employeeId}
                addToGroups={addToGroups}
                isAddingEmployee={isAddingEmployee}
                submitOnboarding={submitOnboarding}
                isHRISActiveEmployeeOnboarding={isHRISActiveEmployeeOnboarding}
                hasAvailableLicenses={hasAvailableLicenses}
                onLicenseSessionReset={() =>
                  setHasAssignedLicenseThisSession(false)
                }
              />
            )}
          </FormProvider>
        </Flex>
      )}
    </OneColumnLayout>
  );
};

export default NewOnboarding;
